import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { useVoiceFixture } from "@/hooks/useVoiceFixture";
import { FixtureBanner } from "@/components/FixtureBanner";
import { api } from "@/lib/apiClient";
import { useAva } from "@/context/AvaContext";
import { DownloadPdfButton } from "@/components/DownloadPdfButton";
import { EmailReportButton } from "@/components/EmailReportButton";
import { Briefcase, Sparkles, Target, Users, Globe, Volume2, Loader2, RefreshCw, HelpCircle, Swords, Radio, Copy, Check, PlugZap, Printer, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const CLUBS = [
  { id: 211, name: "Benfica" },
  { id: 212, name: "Porto" },
  { id: 228, name: "Sporting CP" },
];

const wColor = (v) => (v >= 80 ? "#ccff00" : v >= 68 ? "#33d17a" : v >= 55 ? "#00e5ff" : v >= 45 ? "#ffb800" : "#ff7300");

const BeliefBar = ({ b, top }) => (
  <div data-testid={`belief-${b.formation}`}>
    <div className="mb-1 flex justify-between text-[11px]">
      <span className={top ? "font-semibold text-white" : "text-white/55"}>{b.formation}{top && " · OCTON's pick"}</span>
      <span style={{ color: wColor(b.win_prob) }} title="Shape belief — OCTON's confidence in this formation, not match win %">{b.win_prob}% <span className="text-white/30">shape · ±{b.uncertainty} · {b.samples} learned</span></span>
    </div>
    <div className="h-2 w-full overflow-hidden rounded-full bg-white/8">
      <div className="h-full rounded-full" style={{ width: `${b.win_prob}%`, background: wColor(b.win_prob), boxShadow: `0 0 10px ${wColor(b.win_prob)}` }} />
    </div>
  </div>
);

const Card = ({ icon: Icon, title, children, testid }) => (
  <div className="card-tac p-5" data-testid={testid}>
    <div className="mb-3 flex items-center gap-2"><Icon size={16} className="txt-cyan" /><h3 className="font-tac text-[11px] tracking-[0.25em] text-white/80">{title}</h3></div>
    {children}
  </div>
);

export default function Briefing() {
  const { speak } = useAva();
  const loc = useLocation();
  const [teamId, setTeamId] = useState((loc.state || {}).team_id || 211);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("generating");
  const poll = useRef(null);
  const briefingRef = useRef(null);

  useVoiceFixture({ setTeamId, onRerun: () => load(teamId, true) });
  const [nextFixture, setNextFixture] = useState(undefined);
  useEffect(() => {
    if (!teamId) return;
    let live = true; setNextFixture(undefined);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Lisbon";
    api.get(`/fixture/next-for-team?team_id=${teamId}&tz=${encodeURIComponent(tz)}`).then((r) => live && setNextFixture(r.data?.fixture || null)).catch(() => live && setNextFixture(null));
    return () => { live = false; };
  }, [teamId]);

  const teamIdRef = useRef(teamId);
  useEffect(() => { teamIdRef.current = teamId; }, [teamId]);
  const load = (id, refresh = false) => {
    api.get(`/briefing/club?team_id=${id}${refresh ? "&refresh=true" : ""}`).then((r) => {
      if (Number(id) !== Number(teamIdRef.current)) return;   // coach already switched club — drop stale briefing
      setData(r.data);
      setStatus(r.data.briefing ? "ready" : "generating");
    });
  };

  useEffect(() => {
    setData(null); setStatus("generating");
    load(teamId);
    clearInterval(poll.current);
    poll.current = setInterval(() => {
      api.get(`/briefing/club?team_id=${teamId}`).then((r) => {
        if (Number(teamId) !== Number(teamIdRef.current)) return;
        if (r.data.briefing) {
          setData(r.data); setStatus("ready"); clearInterval(poll.current);
        }
      });
    }, 3500);
    return () => clearInterval(poll.current);
  }, [teamId]);

  const b = data?.briefing;
  const club = data?.club;
  const [training, setTraining] = useState(null);
  const [copied, setCopied] = useState(false);
  const [h2h, setH2h] = useState(null);
  const [h2hRival, setH2hRival] = useState(null);
  const [h2hLoading, setH2hLoading] = useState(false);

  const loadH2h = (rivalId) => {
    setH2hRival(rivalId); setH2h(null); setH2hLoading(true);
    api.get(`/briefing/h2h?team_id=${teamId}&rival_id=${rivalId}`).then((r) => setH2h(r.data)).catch(() => toast.error("Couldn't load head-to-head.")).finally(() => setH2hLoading(false));
  };

  useEffect(() => {
    setTraining(null); setCopied(false); setH2h(null); setH2hRival(null);
    api.get(`/training/status?team_id=${teamId}`).then((r) => setTraining(r.data)).catch(() => {});
  }, [teamId]);

  const copyRequest = () => {
    const s = training?.request_spec;
    if (!s) return;
    const txt = `OCTON — Read-only Live Training Data access request (${training.club})\n\nScope: ${s.scope}\n\nConnection (either):\n${s.connection.map((x) => "• " + x).join("\n")}\n\nData needed (per player, per session):\n${s.data_per_player_per_session.map((x) => "• " + x).join("\n")}\n\nFormat: ${s.format}\nSecurity: ${s.security}\n\nWhy: ${s.why}`;
    navigator.clipboard?.writeText(txt);
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  const briefAloud = () => {
    if (!b) return;
    speak(`${b.headline}. ${b.octon_verdict} Opening line for your meeting: ${b.one_liner_to_open}`, { force: true });
  };

  const fileToFleet = () => {
    api.post(`/briefing/file-to-fleet?team_id=${teamId}`).then((r) => {
      toast.success(r.data.ok ? "Filed to the AVA common brain — the whole fleet can recall it now." : "Filed (queued to fleet).");
    }).catch(() => toast.error("Couldn't reach the fleet — try again."));
  };

  const exportBriefing = () => {
    if (!b) return;
    const list = (arr) => (arr || []).map((x) => `<li>${x}</li>`).join("");
    const bl = (data?.beliefs || []).map((x) => `<li>${x.formation} — <b>${x.win_prob}%</b> (±${x.uncertainty}, ${x.samples} matches learned)</li>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>OCTON Briefing — ${club?.name || ""}</title>
      <style>body{font-family:Georgia,serif;max-width:760px;margin:32px auto;color:#111;line-height:1.5;padding:0 20px}
      h1{font-size:22px;margin:0 0 4px} h2{font-size:13px;letter-spacing:.15em;text-transform:uppercase;color:#0a7;margin:22px 0 6px;border-bottom:1px solid #ddd;padding-bottom:4px}
      .sub{color:#666;font-size:12px;margin-bottom:16px} .open{border-left:3px solid #0a7;padding:8px 14px;background:#f6faf9;font-style:italic;margin:10px 0}
      ul{margin:6px 0 0 18px;padding:0} li{margin:4px 0} .foot{margin-top:28px;color:#999;font-size:11px}</style></head>
      <body onload="window.print()">
      <h1>${b.headline || (club?.name + " — Manager Briefing")}</h1>
      <div class="sub">${club?.name || ""} · Prepared by OCTON Coach Companion · ${new Date().toLocaleDateString()}</div>
      <div class="open">Open with: "${b.one_liner_to_open || ""}"</div>
      <h2>OCTON's Verdict</h2><p>${b.octon_verdict || ""}</p>
      <h2>Tactical Identity</h2><p>${b.tactical_identity || ""}</p>
      <h2>OCTON's Learned Beliefs (win-probability by shape)</h2><ul>${bl}</ul>
      <h2>Talking Points</h2><ul>${list(b.talking_points)}</ul>
      <h2>Questions To Ask</h2><ul>${list(b.questions_to_ask)}</ul>
      <h2>Squad Notes</h2><ul>${list(b.squad_notes)}</ul>
      <h2>Rival Threats</h2><ul>${(b.rivals || []).map((r) => `<li><b>${r.name}:</b> ${r.threat}</li>`).join("")}</ul>
      <h2>European Context</h2><p>${b.european_context || ""}</p>
      <div class="foot">${data?.source || ""}</div>
      </body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); } else { toast.error("Allow pop-ups to export."); }
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8" data-testid="briefing-page" data-octon-fixture={teamId ? String(teamId) : ""} data-octon-fixture-label={data?.club?.name || data?.club_name || (teamId === 211 ? "SL Benfica" : teamId === 212 ? "FC Porto" : teamId === 228 ? "Sporting CP" : "")}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-tac text-[10px] tracking-[0.3em] txt-cyan">MANAGER MEETING · DEEP INTEL</p>
          <h1 className="mt-1 font-head text-4xl font-black tracking-tight sm:text-5xl">Manager Briefing</h1>
          <p className="mt-2 max-w-xl text-sm text-white/50">OCTON fuses its 118-season learned beliefs with the current scene and the AVA common brain — so you walk in on top of the knowledge.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={briefAloud} disabled={!b} data-testid="brief-aloud-btn" className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 px-4 py-2 text-xs text-cyan-300 transition hover:bg-cyan-400/10 disabled:opacity-40"><Volume2 size={14} /> Brief me aloud</button>
          {b && <DownloadPdfButton targetRef={briefingRef} title="Manager Briefing" subtitle={`${club?.name || "Club"} — Manager Briefing`} filename="octon-manager-briefing.pdf" testid="briefing-pdf-btn" />}
          {b && <EmailReportButton targetRef={briefingRef} kind="briefing" title="Manager Briefing" subtitle={`${club?.name || "Club"} — Manager Briefing`} testid="briefing-email-btn" />}
          <button onClick={exportBriefing} disabled={!b} data-testid="export-btn" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-white/60 transition hover:text-white disabled:opacity-40"><Printer size={14} /> Export</button>
          <button onClick={fileToFleet} disabled={!b} data-testid="file-fleet-btn" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-white/60 transition hover:text-white disabled:opacity-40"><UploadCloud size={14} /> File to fleet</button>
          <button onClick={() => { setStatus("generating"); setData((d) => d && { ...d, briefing: null }); load(teamId, true); }} data-testid="refresh-btn" className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-white/60 transition hover:text-white"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      {/* club chips */}
      <div className="mt-6 flex flex-wrap gap-2">
        {CLUBS.map((c) => (
          <button key={c.id} onClick={() => setTeamId(c.id)} data-testid={`club-chip-${c.id}`}
            className={`rounded-full px-5 py-2 text-sm font-semibold transition ${teamId === c.id ? "bg-white text-black" : "border border-white/10 text-white/60 hover:text-white"}`}>
            {c.name}
          </button>
        ))}
      </div>

      {/* club facts + OCTON beliefs (instant) */}
      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card icon={Briefcase} title="CLUB · CURRENT REALITY" testid="club-facts">
          {club ? (
            <div className="space-y-2 text-sm">
              <p className="font-head text-2xl font-black">{club.name}</p>
              <p className="text-white/70"><span className="text-white/40">Coach:</span> {club.manager}</p>
              <p className="text-white/70"><span className="text-white/40">System:</span> {club.system}</p>
              <p className="text-[13px] leading-relaxed text-white/55">{club.identity}</p>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(club.key_players || []).slice(0, 6).map((p, i) => <span key={i} className="rounded-md bg-white/5 px-2 py-1 text-[11px] text-white/60">{p.split(" (")[0]}</span>)}
              </div>
            </div>
          ) : <Loader2 className="animate-spin text-white/30" size={16} />}
        </Card>

        <Card icon={Target} title="OCTON'S LEARNED BELIEFS · SHAPE CONFIDENCE BY FORMATION (NOT MATCH WIN %)" testid="octon-beliefs">
          {data?.beliefs ? (
            <div className="space-y-2.5">
              {data.beliefs.map((bl, i) => <BeliefBar key={bl.formation} b={bl} top={i === 0} />)}
              <p className="pt-1 text-[11px] leading-snug text-white/45">Independently learned from 118 seasons of Bayesian/Hebbian training — not hand-set. Compare this to their real system to see where OCTON agrees or diverges.</p>
            </div>
          ) : <Loader2 className="animate-spin text-white/30" size={16} />}
        </Card>
      </div>

      {/* live squad layer — this week's real available XI + injuries */}
      {data?.live_squad && (
        <div className="mt-4">
          <Card icon={Users} title={`THIS WEEK'S SQUAD · REAL AVAILABILITY (${data.live_squad.shape})`} testid="live-squad">
            {data.live_squad.actual_shape?.base && (
              <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="briefing-actual-shape">
                <span className="rounded-full border border-[#ccff00]/30 bg-[#ccff00]/[0.07] px-2.5 py-1 font-tac text-[9px] tracking-widest" style={{ color: "#ccff00" }}>
                  REAL BASE SHAPE · {data.live_squad.actual_shape.base}
                  {data.live_squad.actual_shape.tally?.[data.live_squad.actual_shape.base] ? ` ×${data.live_squad.actual_shape.tally[data.live_squad.actual_shape.base]}` : ""}
                  {data.live_squad.actual_shape.matches ? ` / last ${data.live_squad.actual_shape.matches}` : ""}
                </span>
                {data.live_squad.actual_shape.last_match && (
                  <span className="rounded-full border border-white/10 px-2.5 py-1 font-tac text-[9px] tracking-widest text-white/50">LAST MATCH · {data.live_squad.actual_shape.last_match}</span>
                )}
                <span className="font-tac text-[9px] tracking-widest text-white/35">from actual lineups</span>
              </div>
            )}
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <p className="mb-2 font-tac text-[9px] tracking-widest text-white/40">STRONGEST AVAILABLE XI</p>
                <div className="grid gap-1.5 sm:grid-cols-2" data-testid="live-squad-xi" data-octon-section="live squad available xi injuries fitness watch" data-octon-label="Live Squad">
                  {data.live_squad.xi.map((pl, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-1.5 text-[12px]">
                      <span className="truncate text-white/80"><span className="mr-1.5 font-mono text-[9px] text-white/35">{pl.position}</span>{pl.name}</span>
                      <span className="shrink-0 font-mono text-[11px]" style={{ color: wColor(pl.readiness) }}>{pl.readiness}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">UNAVAILABLE / INJURED</p>
                  {data.live_squad.unavailable.length ? (
                    <ul className="space-y-1" data-testid="live-squad-out">{data.live_squad.unavailable.map((x, i) => (
                      <li key={i} className="text-[12px] text-red-300">⚕ {x.name}<span className="text-white/40"> — {x.reason}</span></li>))}</ul>
                  ) : <p className="text-[12px] text-white/45">No absences reported.</p>}
                </div>
                {data.live_squad.watch.length > 0 && (
                  <div>
                    <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">FITNESS WATCH</p>
                    <ul className="space-y-1" data-testid="live-squad-watch">{data.live_squad.watch.map((x, i) => (
                      <li key={i} className="text-[12px] text-white/65">{x.name} <span className="font-mono" style={{ color: wColor(x.readiness) }}>{x.readiness}%</span> <span className="text-white/35">{x.status.toLowerCase()}</span></li>))}</ul>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-3 text-[11px] leading-snug text-white/40">Real current squad, minutes-load and injuries — OCTON names these exact players in its squad notes below so the briefing reflects who is actually available this week.</p>
          </Card>
        </div>
      )}

      {/* rivals */}
      {data?.rival_beliefs && Object.keys(data.rival_beliefs).length > 0 && (
        <div className="mt-4">
          <Card icon={Swords} title="RIVALS · OCTON'S READ OF THEIR SHAPES" testid="rivals-beliefs">
            <div className="grid gap-4 sm:grid-cols-2">
              {Object.entries(data.rival_beliefs).map(([rid, rv]) => (
                <div key={rid}>
                  <p className="mb-2 text-sm font-semibold text-white/80">{rv.name}</p>
                  <div className="space-y-2">{rv.beliefs.slice(0, 3).map((bl, i) => <BeliefBar key={bl.formation} b={bl} top={i === 0} />)}</div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* head-to-head memory */}
      {data?.rival_beliefs && Object.keys(data.rival_beliefs).length > 0 && (
        <div className="mt-4">
          <Card icon={Swords} title="HEAD-TO-HEAD MEMORY · OCTON'S COUNTER-PLAN" testid="h2h-card">
            <div className="mb-3 flex flex-wrap gap-2">
              {Object.entries(data.rival_beliefs).map(([rid, rv]) => (
                <button key={rid} onClick={() => loadH2h(Number(rid))} data-testid={`h2h-rival-${rid}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${h2hRival === Number(rid) ? "bg-cyan-400 text-black" : "border border-white/10 text-white/60 hover:text-white"}`}>
                  vs {rv.name}
                </button>
              ))}
            </div>
            {!h2hRival ? (
              <p className="text-[12px] text-white/45">Pick a rival to get OCTON's head-to-head read — how they set up and the exact counter-shape to beat them.</p>
            ) : h2hLoading || !h2h?.h2h ? (
              <div className="flex items-center gap-2 text-[12px] text-white/50"><Loader2 className="animate-spin txt-cyan" size={15} /> OCTON is recalling the matchup and computing a counter-plan…</div>
            ) : (
              <div className="space-y-3" data-testid="h2h-content" data-octon-section="head to head h2h memory rival counter shape" data-octon-label="Head-to-Head Memory">
                <p className="text-[13px] leading-relaxed text-white/70"><span className="text-white/40">History: </span>{h2h.h2h.history}</p>
                <p className="text-[13px] leading-relaxed text-white/70"><span className="text-white/40">Their likely setup: </span>{h2h.h2h.their_setup}</p>
                <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/[0.06] p-4">
                  <p className="font-tac text-[9px] tracking-widest txt-cyan">OCTON'S COUNTER-SHAPE</p>
                  <p className="mt-1 font-head text-2xl font-black">{h2h.h2h.our_counter?.formation}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-white/75">{h2h.h2h.our_counter?.why}</p>
                </div>
                {h2h.h2h.key_battles?.length > 0 && (
                  <div>
                    <p className="mb-1 font-tac text-[9px] tracking-widest text-white/40">KEY BATTLES</p>
                    <ul className="space-y-1">{h2h.h2h.key_battles.map((x, i) => <li key={i} className="flex gap-2 text-[13px] text-white/70"><span className="txt-cyan">▸</span>{x}</li>)}</ul>
                  </div>
                )}
                {h2h.h2h.edge && <p className="rounded-lg border-l-2 border-[#ccff00] bg-black/30 px-3 py-2 text-[13px] italic text-white/85">Decisive edge: {h2h.h2h.edge}</p>}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Live training data connector */}
      <div className="mt-4">
        <Card icon={PlugZap} title="LIVE TRAINING DATA · READ-ONLY CONNECTOR" testid="training-connector">
          {!training ? <Loader2 className="animate-spin text-white/30" size={16} /> : training.connected ? (
            <div className="flex items-center gap-3 text-sm">
              <span className="h-2.5 w-2.5 rounded-full bg-[#33d17a] glow-cyan" />
              <p className="text-white/80">Connected to <span className="font-semibold">{training.provider}</span> — GPS/load & wellness feeding OCTON's readiness & injury models. Last sync {training.last_sync || "—"}.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-start gap-3">
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#ffb800]" />
                <p className="text-sm text-white/70">
                  <span className="text-white">Not connected.</span> {training.club}'s training-ground data (GPS, load, wellness) is private — it needs <span className="txt-cyan">read-only access granted by the club</span>. I probed the AVA fleet &amp; mothership: it isn't available there. Hand this request to the manager and I'll plug it in the moment you have credentials.
                </p>
              </div>
              <div className="mt-3 rounded-xl border border-white/10 bg-black/30 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="font-tac text-[9px] tracking-widest txt-cyan">ACCESS REQUEST — READY TO HAND OVER</p>
                  <button onClick={copyRequest} data-testid="copy-request-btn" className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 px-3 py-1 text-[11px] text-cyan-300 hover:bg-cyan-400/10">
                    {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                  </button>
                </div>
                <p className="text-[12px] text-white/60"><span className="text-white/40">Scope:</span> {training.request_spec.scope}</p>
                <p className="mt-2 text-[11px] font-semibold text-white/50">Data needed (per player, per session):</p>
                <ul className="mt-1 space-y-1">
                  {training.request_spec.data_per_player_per_session.map((x, i) => (
                    <li key={i} className="flex gap-2 text-[12px] text-white/65"><span className="txt-cyan">▸</span>{x}</li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-white/45"><span className="text-white/35">Format:</span> {training.request_spec.format} · <span className="text-white/35">Security:</span> {training.request_spec.security}</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {teamId && <FixtureBanner fixture={nextFixture} teamName={club?.name} />}

      {/* synthesised briefing */}
      <div className="mt-4">
        {status !== "ready" || !b ? (
          <div className="card-tac flex items-center gap-3 p-6 text-sm text-white/60" data-testid="briefing-loading" data-octon-section="briefing manager meeting talking points verdict" data-octon-label="Manager Briefing" data-octon-state="loading">
            <Loader2 className="animate-spin txt-cyan" size={18} />
            OCTON is synthesising your briefing — cross-referencing its brain, the current scene and the AVA common brain…
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="briefing-content" data-octon-section="briefing manager meeting talking points verdict" data-octon-label="Manager Briefing" ref={briefingRef}>
            <div className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-400/[0.07] to-transparent p-6">
              <p className="font-head text-2xl font-black leading-tight">{b.headline}</p>
              <p className="mt-3 text-[15px] leading-relaxed text-white/80">{b.octon_verdict}</p>
              {b.one_liner_to_open && (
                <div className="mt-4 rounded-xl border-l-2 border-cyan-400 bg-black/30 px-4 py-3">
                  <p className="font-tac text-[9px] tracking-widest txt-cyan">OPEN THE MEETING WITH</p>
                  <p className="mt-1 text-[15px] italic text-white/90">"{b.one_liner_to_open}"</p>
                </div>
              )}
            </div>

            {b.tactical_identity && (
              <Card icon={Target} title="TACTICAL IDENTITY" testid="tactical-identity"><p className="text-[13px] leading-relaxed text-white/70">{b.tactical_identity}</p></Card>
            )}

            <div className="grid gap-4 lg:grid-cols-2">
              {b.talking_points?.length > 0 && (
                <Card icon={Sparkles} title="TALKING POINTS · SOUND EXPERT" testid="talking-points">
                  <ul className="space-y-2">{b.talking_points.map((x, i) => <li key={i} className="flex gap-2 text-[13px] text-white/70"><span className="txt-cyan">▸</span>{x}</li>)}</ul>
                </Card>
              )}
              {b.questions_to_ask?.length > 0 && (
                <Card icon={HelpCircle} title="INCISIVE QUESTIONS TO ASK" testid="questions">
                  <ul className="space-y-2">{b.questions_to_ask.map((x, i) => <li key={i} className="flex gap-2 text-[13px] text-white/70"><span className="text-[#ffb800]">?</span>{x}</li>)}</ul>
                </Card>
              )}
            </div>

            {b.squad_notes?.length > 0 && (
              <Card icon={Users} title="SQUAD NOTES" testid="squad-notes">
                <div className="grid gap-2 sm:grid-cols-2">{b.squad_notes.map((x, i) => <p key={i} className="rounded-lg bg-white/[0.03] px-3 py-2 text-[13px] text-white/70">{x}</p>)}</div>
              </Card>
            )}

            {b.rivals?.length > 0 && (
              <Card icon={Swords} title="RIVAL THREATS" testid="rival-threats">
                <div className="space-y-2">{b.rivals.map((r, i) => <p key={i} className="text-[13px] text-white/70"><span className="font-semibold text-white">{r.name}:</span> {r.threat}</p>)}</div>
              </Card>
            )}

            {b.european_context && (
              <Card icon={Globe} title="EUROPEAN CONTEXT" testid="european-context"><p className="text-[13px] leading-relaxed text-white/70">{b.european_context}</p></Card>
            )}

            <p className="pt-1 text-center text-[11px] text-white/30">{data.source}</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
